import type {
    AppNotification,
    NotificationChannel,
    NotificationChannelPreferences,
    NotificationEventType,
    NotificationEntityType,
    NotificationMute,
    NotificationPreferenceKey,
    NotificationSurface,
    NotificationSeverity,
    UserNotificationPreferences,
} from '~/types/models';

export type NotificationDefinition = {
    eventType: NotificationEventType,
    surface: NotificationSurface,
    label: string,
    description: string,
    defaultChannels: NotificationChannelPreferences,
    defaultSeverity?: NotificationSeverity,
};

const channelFlags = (channels: NotificationChannel[]): NotificationChannelPreferences => ({
    inApp: channels.includes('in_app'),
    push: channels.includes('push'),
    email: channels.includes('email'),
});

export const NOTIFICATION_DEFINITIONS: NotificationDefinition[] = [
    {
        eventType: 'task_assigned',
        surface: 'tenant',
        label: 'Task assigned to me',
        description: 'When a task is assigned directly to you.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
    {
        eventType: 'task_comment_reply',
        surface: 'tenant',
        label: 'Task comment or reply',
        description: 'When someone replies or comments on a task you are involved with.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
    {
        eventType: 'task_response_received',
        surface: 'tenant',
        label: 'Client responded to task request',
        description: 'When a client replies to a shared task request.',
        defaultChannels: channelFlags(['in_app', 'push']),
        defaultSeverity: 'warning',
    },
    {
        eventType: 'task_due_soon',
        surface: 'tenant',
        label: 'Task due soon',
        description: 'When your assigned task is coming up soon.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
    {
        eventType: 'task_overdue',
        surface: 'tenant',
        label: 'Task overdue',
        description: 'When an assigned task passes its due date.',
        defaultChannels: channelFlags(['in_app', 'push']),
        defaultSeverity: 'warning',
    },
    {
        eventType: 'client_invoice_overdue',
        surface: 'tenant',
        label: 'Client invoice overdue',
        description: 'When a client has overdue invoice balance that needs attention.',
        defaultChannels: channelFlags(['in_app', 'push']),
        defaultSeverity: 'warning',
    },
    {
        eventType: 'client_reengagement_due',
        surface: 'tenant',
        label: 'Client re-engagement due',
        description: 'When a client has been inactive long enough to warrant a follow-up.',
        defaultChannels: channelFlags(['in_app', 'push']),
        defaultSeverity: 'warning',
    },
    {
        eventType: 'approval_requested',
        surface: 'tenant',
        label: 'Approval requested',
        description: 'When client approval is needed on work you manage.',
        defaultChannels: channelFlags(['in_app', 'push']),
        defaultSeverity: 'warning',
    },
    {
        eventType: 'approval_responded',
        surface: 'tenant',
        label: 'Approval responded to',
        description: 'When a client approves or rejects a request.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
    {
        eventType: 'quote_status_changed',
        surface: 'tenant',
        label: 'Quote accepted or rejected',
        description: 'When a quote changes status after client review.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
    {
        eventType: 'invoice_paid',
        surface: 'tenant',
        label: 'Invoice paid',
        description: 'When a client payment lands successfully.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
    {
        eventType: 'client_message_received',
        surface: 'tenant',
        label: 'New client message',
        description: 'When a client sends a new message.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
    {
        eventType: 'project_published',
        surface: 'tenant',
        label: 'Project published to portal',
        description: 'When a project becomes visible in the client portal.',
        defaultChannels: channelFlags(['in_app']),
    },
    {
        eventType: 'team_message_received',
        surface: 'client',
        label: 'New team message',
        description: 'When your team sends a new message in the portal.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
    {
        eventType: 'message_response_reminder',
        surface: 'client',
        label: 'Message response reminder',
        description: 'When the team is waiting for your reply to a conversation message.',
        defaultChannels: channelFlags(['in_app', 'push', 'email']),
        defaultSeverity: 'warning',
    },
    {
        eventType: 'approval_requested',
        surface: 'client',
        label: 'Approval waiting',
        description: 'When there is work waiting for your review.',
        defaultChannels: channelFlags(['in_app', 'push', 'email']),
        defaultSeverity: 'warning',
    },
    {
        eventType: 'quote_ready',
        surface: 'client',
        label: 'Quote ready',
        description: 'When a quote is ready for review.',
        defaultChannels: channelFlags(['in_app', 'push', 'email']),
    },
    {
        eventType: 'invoice_issued',
        surface: 'client',
        label: 'Invoice issued',
        description: 'When a new invoice is available.',
        defaultChannels: channelFlags(['in_app', 'push', 'email']),
    },
    {
        eventType: 'invoice_due',
        surface: 'client',
        label: 'Invoice due',
        description: 'When an invoice is approaching or past due.',
        defaultChannels: channelFlags(['in_app', 'push', 'email']),
        defaultSeverity: 'warning',
    },
    {
        eventType: 'task_action_required',
        surface: 'client',
        label: 'Task action required',
        description: 'When the team is waiting on your response or upload.',
        defaultChannels: channelFlags(['in_app', 'push', 'email']),
        defaultSeverity: 'warning',
    },
    {
        eventType: 'project_live',
        surface: 'client',
        label: 'Project live in portal',
        description: 'When a project is published and ready to view.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
    {
        eventType: 'contract_ready',
        surface: 'client',
        label: 'Contract ready to sign',
        description: 'When a contract needs your signature.',
        defaultChannels: channelFlags(['in_app', 'push', 'email']),
        defaultSeverity: 'critical',
    },
    {
        eventType: 'project_milestone_updated',
        surface: 'client',
        label: 'Project milestone update',
        description: 'When a project reaches an important milestone.',
        defaultChannels: channelFlags(['in_app', 'push']),
    },
];

export const TENANT_NOTIFICATION_DEFINITIONS = NOTIFICATION_DEFINITIONS.filter((item) => item.surface === 'tenant');
export const CLIENT_NOTIFICATION_DEFINITIONS = NOTIFICATION_DEFINITIONS.filter((item) => item.surface === 'client');

export function notificationPreferenceKey(surface: NotificationSurface, eventType: NotificationEventType): NotificationPreferenceKey {
    return `${surface}:${eventType}`;
}

export function normalizeNotificationPreferences(preferences?: UserNotificationPreferences | null): UserNotificationPreferences {
    const defaults = buildDefaultNotificationPreferences();
    const mergedEvents: Partial<Record<NotificationPreferenceKey, NotificationChannelPreferences>> = {
        ...(defaults.events || {}),
    };

    for (const definition of NOTIFICATION_DEFINITIONS) {
        const scopedKey = notificationPreferenceKey(definition.surface, definition.eventType);
        const scopedValue = preferences?.events?.[scopedKey];
        const legacyValue = preferences?.events?.[definition.eventType as NotificationPreferenceKey];

        mergedEvents[scopedKey] = {
            ...(mergedEvents[scopedKey] || {}),
            ...(legacyValue || {}),
            ...(scopedValue || {}),
        };
    }

    return {
        ...defaults,
        ...(preferences || {}),
        events: mergedEvents,
    };
}

export function buildDefaultNotificationPreferences(): UserNotificationPreferences {
    return {
        weeklyDigest: true,
        events: Object.fromEntries(
            NOTIFICATION_DEFINITIONS.map((definition) => [
                notificationPreferenceKey(definition.surface, definition.eventType),
                { ...definition.defaultChannels },
            ]),
        ) as Partial<Record<NotificationPreferenceKey, NotificationChannelPreferences>>,
        updatedAt: new Date(),
    };
}

export function resolveNotificationChannels(
    surface: NotificationSurface,
    eventType: NotificationEventType,
    preferences?: UserNotificationPreferences | null,
): NotificationChannel[] {
    const definition = NOTIFICATION_DEFINITIONS.find((item) => item.surface === surface && item.eventType === eventType);
    const normalizedPreferences = normalizeNotificationPreferences(preferences);
    const eventPreferences = normalizedPreferences.events?.[notificationPreferenceKey(surface, eventType)] || definition?.defaultChannels || {};
    return [
        ...(eventPreferences.inApp === false ? [] : ['in_app' as const]),
        ...(eventPreferences.push ? ['push' as const] : []),
        ...(eventPreferences.email ? ['email' as const] : []),
    ];
}

export function matchesNotificationContext(
    notification: AppNotification,
    context: { surface: NotificationSurface; tenantId?: string | null; clientId?: string | null; projectId?: string | null },
): boolean {
    if (notification.surface !== context.surface) {
        return false;
    }

    if (context.tenantId && notification.tenantId !== context.tenantId) {
        return false;
    }

    if (context.clientId && notification.clientId !== context.clientId) {
        return false;
    }

    if (context.projectId && notification.projectId !== context.projectId) {
        return false;
    }

    return true;
}

export function isNotificationUnread(notification: AppNotification): boolean {
    return !notification.readAt && !notification.dismissedAt;
}

export function isNotificationDismissed(notification: AppNotification): boolean {
    return Boolean(notification.dismissedAt);
}

function notificationMuteId(parts: string[]): string {
    return encodeURIComponent(parts.map((part) => String(part || '').trim()).join(':'));
}

export function notificationClientMuteId(tenantId: string, clientId: string): string {
    return notificationMuteId(['client', tenantId, clientId]);
}

export function notificationEntityMuteId(tenantId: string, entityType: NotificationEntityType, entityId: string): string {
    return notificationMuteId(['entity', tenantId, entityType, entityId]);
}

export function matchesNotificationMute(notification: AppNotification, mute: NotificationMute): boolean {
    if (notification.tenantId !== mute.tenantId) return false;

    if (mute.scope === 'client') {
        return Boolean(mute.clientId) && notification.clientId === mute.clientId;
    }

    if (!mute.entityType || !mute.entityId) return false;
    if (notification.entityType === mute.entityType && notification.entityId === mute.entityId) return true;

    return mute.entityType === 'project' && notification.projectId === mute.entityId;
}

export type NotificationPageKey = 'messages' | 'tasks' | 'projects' | 'clients' | 'quotes' | 'invoices';
export type ClientNotificationTab = 'overview' | 'people' | 'delivery' | 'finance' | 'comms' | 'activity' | 'automation';
export type ProjectNotificationTab = 'overview' | 'tasks' | 'delivery' | 'finance' | 'activity';

export function notificationPageKey(notification: AppNotification): NotificationPageKey | null {
    const path = String(notification.actionPath || '').toLowerCase();
    if (path.includes('/messages')) return 'messages';
    if (path.includes('/tasks')) return 'tasks';
    if (path.includes('/quotes')) return 'quotes';
    if (path.includes('/invoices')) return 'invoices';
    if (path.includes('/clients/projects/') || path.includes('/projects/')) return 'projects';
    if (path.includes('/clients')) return 'clients';

    if (notification.entityType === 'task' || notification.eventType.startsWith('task_')) return 'tasks';
    if (notification.entityType === 'message' || notification.eventType.includes('message')) return 'messages';
    if (notification.entityType === 'quote' || notification.eventType.startsWith('quote_')) return 'quotes';
    if (notification.entityType === 'invoice' || notification.eventType.startsWith('invoice_')) return 'invoices';
    if (notification.entityType === 'client' || notification.eventType.startsWith('client_')) return 'clients';
    if (notification.projectId || ['project', 'milestone', 'approval', 'contract'].includes(String(notification.entityType || ''))) return 'projects';
    return null;
}

export function clientNotificationTab(notification: AppNotification): ClientNotificationTab {
    if (notification.entityType === 'quote' || notification.entityType === 'invoice' || notification.eventType.includes('invoice') || notification.eventType.includes('quote')) return 'finance';
    if (notification.entityType === 'message' || notification.entityType === 'comment' || notification.eventType.includes('message') || notification.eventType === 'client_reengagement_due') return 'comms';
    if (['task', 'approval', 'project', 'contract', 'milestone'].includes(String(notification.entityType || '')) || notification.eventType.startsWith('task_') || notification.eventType.startsWith('approval_') || notification.eventType.startsWith('project_')) return 'delivery';
    return 'overview';
}

export function projectNotificationTab(notification: AppNotification): ProjectNotificationTab {
    if (notification.entityType === 'quote' || notification.entityType === 'invoice' || notification.eventType.includes('invoice') || notification.eventType.includes('quote')) return 'finance';
    if (notification.entityType === 'message' || notification.entityType === 'comment' || notification.eventType.includes('message')) return 'activity';
    if (notification.entityType === 'contract') return 'delivery';
    if (notification.entityType === 'task' || notification.entityType === 'approval' || notification.eventType.startsWith('task_') || notification.eventType.startsWith('approval_')) return 'tasks';
    return 'overview';
}

export function isInactiveNotificationStatus(status: unknown): boolean {
    return ['done', 'complete', 'completed', 'archived', 'cancelled', 'canceled'].includes(String(status || '').trim().toLowerCase());
}

export function notificationAccentClass(notification: AppNotification): string {
    if (notification.severity === 'critical') {
        return 'border-destructive/35 bg-destructive/5';
    }

    if (notification.severity === 'warning') {
        return 'border-amber-400/40 bg-amber-50/65 dark:bg-amber-950/20';
    }

    return 'border-border/70 bg-card';
}
